import { Button } from "@egaranti/components";
import { Input } from "@egaranti/components";
import { Label } from "@egaranti/components";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@egaranti/components";
import { useToast } from "@egaranti/components";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@egaranti/components";
import { zodResolver } from "@hookform/resolvers/zod";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import employeesService from "@/services/employeesService";

import PermissionsCard from "@/components/settings/employees/permissionsCard";
import { Breadcrumb, BreadcrumbItem } from "@/components/shared/breadcrumbs";
import CheckRole from "@/components/shared/checkRole";

import { emptyPermissionState } from "@/lib/employeePermissions";

import * as z from "zod";

const CreateEmployee = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [permissions, setPermissions] = useState(emptyPermissionState);

  const form = useForm({
    mode: "onBlur",
    resolver: zodResolver(
      z.object({
        firstName: z.string().nonempty({ message: t("common.nonEmpty") }),
        lastName: z.string().nonempty({ message: t("common.nonEmpty") }),
        email: z.string().email({ message: t("common.validEmail") }),
        password: z
          .string()
          .min(8, { message: t("common.validateMsg.passwordMin") })
          .max(50, {
            message: t("common.validateMsg.passwordMax"),
          }),
        passwordConfirm: z
          .string({ message: t("common.nonEmpty") })
          .min(8, { message: t("common.validateMsg.passwordMin") })
          .max(50, {
            message: t("common.validateMsg.passwordMax"),
          })
          .refine((data) => data.password === data.passwordConfirm, {
            message: t("common.passwordsNotMatch"),
            path: ["passwordConfirm"],
          }),
        phone: z
          .string()
          .min(10, { message: t("common.validateMsg.phoneNumber") })
          .max(10, { message: t("common.validateMsg.phoneNumber") }),
        countryCode: z.string().min(1, { message: t("common.nonEmpty") }),
        userType: z.string().min(1, { message: t("common.nonEmpty") }),
        sellerCode: z
          .string()
          .optional()
          .superRefine((val, ctx) => {
            if (
              form.getValues("userType") === "STORE" &&
              (!val || val.trim() === "")
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("common.nonEmpty"),
              });
            }
          }),
      }),
    ),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      passwordConfirm: "",
      phone: "",
      countryCode: "TR",
      userType: "USER",
      sellerCode: "",
    },
  });

  const onSubmit = (data) => {
    const bodyObj = {
      ...data,
      roles: permissions,
    };

    // If userType is STORE, ensure sellerCode is included
    if (data.userType === "STORE" && !data.sellerCode) {
      toast({
        title: t("settingsPage.sellerCodeRequired", "Bayi kodu zorunludur"),
        variant: "error",
      });
      return;
    }

    employeesService
      .addMerchantUser(bodyObj)
      .then(() => {
        navigate("/settings/employees");
        toast({
          title: t("settingsPage.employeeAddedSuccessfully"),
          variant: "success",
        });
      })
      .catch((err) => {
        toast({
          title:
            err?.response?.data?.errors?.[0]?.message ??
            t("common.unexpectedError", "Beklenmeyen bir hata oluştu"),
          variant: "error",
        });
      });
  };

  return (
    <main className="flex min-h-screen flex-col gap-5 bg-[#F9FAFC]">
      <Breadcrumb>
        <BreadcrumbItem link="/settings/employees">
          {t("settingsPage.employees")}
        </BreadcrumbItem>
        <BreadcrumbItem active>{t("settingsPage.addEmployee")}</BreadcrumbItem>
      </Breadcrumb>
      <div className="container mx-auto flex flex-col items-center justify-center gap-6">
        <div className="formBox flex-col gap-4">
          <h2 className="mb-5 font-medium">{t("settingsPage.employeeInfo")}</h2>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="grid w-full grid-cols-1 gap-4 md:grid-cols-2"
            >
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <Label>{t("common.firstName")}</Label>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <Label>{t("common.lastName")}</Label>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <Label>{t("common.email")}</Label>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <Label>{t("common.password")}</Label>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="passwordConfirm"
                render={({ field }) => (
                  <FormItem>
                    <Label>{t("common.passwordConfirm")}</Label>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex gap-4">
                <FormField
                  control={form.control}
                  name="countryCode"
                  render={({ field }) => (
                    <FormItem>
                      <Label>{t("settingsPage.country")}</Label>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="TR">+90</SelectItem>
                          <SelectItem value="UK">+44</SelectItem>
                          <SelectItem value="UZ">+998</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <Label>{t("settingsPage.phoneNumber")}</Label>
                      <FormControl>
                        <Input type="tel" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="userType"
                render={({ field }) => (
                  <FormItem>
                    <Label>{t("settingsPage.type", "Kullanıcı Tipi")}</Label>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        form.trigger("sellerCode");
                      }}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="USER">
                          {t("settingsPage.user", "Kullanıcı")}
                        </SelectItem>
                        <SelectItem value="STORE">
                          {t("settingsPage.store", "Bayi")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.watch("userType") === "STORE" && (
                <FormField
                  control={form.control}
                  name="sellerCode"
                  render={({ field }) => (
                    <FormItem>
                      <Label>{t("settingsPage.sellerCode", "Bayi Kodu")}</Label>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </form>
          </Form>
        </div>
      </div>
      <div className="container mx-auto flex flex-col items-center justify-center gap-6">
        <PermissionsCard
          permissions={permissions}
          onChange={setPermissions}
        />
        <CheckRole section="PERSONALS" action="EDIT">
          <Button className="mb-20 ml-auto" onClick={form.handleSubmit(onSubmit)}>
            {t("common.save")}
          </Button>
        </CheckRole>
      </div>
    </main>
  );
};

export default CreateEmployee;
